import React, { useEffect, useRef, useState } from "react";
import {
  Archive,
  Building2,
  ChevronDown,
  Database,
  ExternalLink,
  KeyRound,
  Lock,
  LogOut,
  type LucideIcon,
  Mail,
  Percent,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Upload,
  Users,
} from "lucide-react";

import LockTypes from "./components/LockTypes";
import LockProperties from "./components/LockProperties";
import SupplierManagement from "./components/SupplierManagement";
import TaxChange from "./components/TaxChange";
import DatabasePreview from "./components/DatabasePreview";
import Backups from "./components/Backups";
import DatabaseUpdate from "./components/DatabaseUpdate";
import UserManagement from "./components/UserManagement";
import SystemOptionsMapping from "./components/SystemOptionsMapping";
import Carts from "./components/Carts";
import MailDelivery, { MailStatusBanner } from "./components/MailDelivery";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import LoginScreen from "./LoginScreen";
import { isAdminLoggedIn, adminLogout, getAdminPermissions, getAdminUsername } from "./adminApi";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { DialogProvider } from "./context/DialogContext";
import "./admin-theme.css";
import "./admin-lists.css";
import "./AdminPanel.css";

export type ViewType =
  | "Lock types"
  | "Lock properties"
  | "Supplier Icon"
  | "Database preview"
  | "Backups"
  | "Tax change"
  | "Database update"
  | "Users"
  | "System options"
  | "Carts"
  | "Mail delivery";

// Uprawnienie wymagane dla danego widoku. "System options" ma własne uprawnienie -
// przy starcie backendu dostają je role, które miały "Lock properties" (migracja w sqlite.js).
// Te same nazwy sprawdza API (Backend/src/middleware/permissions.js).
export const viewPermission: Record<ViewType, string> = {
  "Lock types": "Lock types",
  "Lock properties": "Lock properties",
  "Supplier Icon": "Supplier Icon",
  "Database preview": "Database preview",
  "Backups": "Backups",
  "Tax change": "Tax change",
  "Database update": "Database update",
  "Users": "Users",
  "System options": "System options",
  // Warenkörbe i Mailversand - domyślnie tylko rola admin (migracja w sqlite.js)
  "Carts": "Carts",
  "Mail delivery": "Mail delivery",
};

export const checkViewAccess = (view: ViewType, permissions: string[]): boolean =>
  permissions.includes(viewPermission[view]);

const VIEW_ICONS: Record<ViewType, LucideIcon> = {
  "Lock types": KeyRound,
  "Lock properties": ShieldCheck,
  "System options": SlidersHorizontal,
  "Supplier Icon": Building2,
  "Database preview": Database,
  "Database update": Upload,
  "Backups": Archive,
  "Tax change": Percent,
  "Users": Users,
  "Carts": ShoppingCart,
  "Mail delivery": Mail,
};

// Menu w grupach - pozycje pogrupowane tematycznie
const MENU_GROUPS: { title: string; items: ViewType[] }[] = [
  { title: "Konfigurator", items: ["Lock types", "Lock properties", "System options", "Supplier Icon"] },
  { title: "Daten", items: ["Database preview", "Database update", "Backups", "Tax change", "Carts"] },
  { title: "Verwaltung", items: ["Users", "Mail delivery"] },
];

const VIEW_ORDER: ViewType[] = MENU_GROUPS.flatMap((group) => group.items);

// Widoki z szerokimi tabelami dostają całą szerokość okna (bez limitu 1280 px) -
// optionstable ma 21 kolumn i w limicie trzeba było przewijać w prawo.
const WIDE_VIEWS: ViewType[] = ["Database preview", "Database update", "Carts"];

const AdminPanelContent: React.FC = () => {
  const { lang, setLang, t } = useLanguage();
  const permissions = getAdminPermissions();

  const [activeView, setActiveView] = useState<ViewType>(
    VIEW_ORDER.find((view) => checkViewAccess(view, permissions)) || "Lock types"
  );
  const [isAuthenticated, setIsAuthenticated] = useState(isAdminLoggedIn());
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Menu użytkownika zamyka się kliknięciem poza nim i klawiszem Escape
  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  // Logowanie tylko przez konto w panelu (logowanie Microsoft zostało skreślone z zakresu)
  const username = getAdminUsername();

  const handleLogout = () => {
    adminLogout();
    setIsAuthenticated(false);
  };

  const renderView = () => {
    if (!checkViewAccess(activeView, permissions)) {
      return <div className="admin-empty">{t("Brak uprawnień do tego widoku")}</div>;
    }

    switch (activeView) {
      case "Lock types":
        return <LockTypes />;
      case "Lock properties":
        return <LockProperties />;
      case "Supplier Icon":
        return <SupplierManagement />;
      case "Tax change":
        return <TaxChange />;
      case "Database preview":
        return <DatabasePreview />;
      case "Backups":
        return <Backups />;
      case "Database update":
        return <DatabaseUpdate />;
      case "Users":
        return <UserManagement />;
      case "System options":
        return <SystemOptionsMapping />;
      case "Carts":
        return <Carts />;
      case "Mail delivery":
        return <MailDelivery />;
      default:
        return <div className="admin-empty">{t("Choose view from the menu")}</div>;
    }
  };

  if (!isAuthenticated) {
    // Po zalogowaniu przeładowanie - uprawnienia i widok startowy z nowego tokenu
    return <LoginScreen onLogin={() => window.location.reload()} />;
  }

  return (
    <div className="admin-panel">
      {/* Komunikaty i okna potwierdzenia widoków - w .admin-panel, bo tam są ich style */}
      <DialogProvider>
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand">
          {/* Logo prowadzi do konfiguratora - nie na "/", bo ta trasa czyści zapisaną konfigurację */}
          <a className="admin-sidebar__home" href="/1" title={t("Zum Konfigurator")}>
            <img className="admin-sidebar__logo" src="/logo-wplusm-2026.svg" alt="WplusM" />
          </a>
          <p className="admin-sidebar__subtitle">{t("Admin-Panel")}</p>
        </div>

        <nav className="admin-nav">
          {MENU_GROUPS.map((group) => {
            const items = group.items.filter((item) => checkViewAccess(item, permissions));
            if (items.length === 0) return null;
            return (
              <div className="admin-nav__group" key={group.title}>
                <p className="admin-nav__group-title">{t(group.title)}</p>
                {items.map((item) => {
                  const Icon = VIEW_ICONS[item];
                  return (
                    <button
                      key={item}
                      type="button"
                      className={`admin-nav__item ${activeView === item ? "admin-nav__item--active" : ""}`}
                      aria-current={activeView === item ? "page" : undefined}
                      onClick={() => setActiveView(item)}
                    >
                      <Icon size={17} strokeWidth={2} />
                      {t(item)}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="admin-sidebar__footer">
          <a className="admin-sidebar__link" href="/1">
            <ExternalLink size={14} /> {t("Zum Konfigurator")}
          </a>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar__breadcrumb">
            {t("Admin-Panel")} <span aria-hidden="true">/</span> <strong>{t(activeView)}</strong>
          </div>

          <div className="admin-topbar__spacer" />

          <div className="admin-segment" role="group" aria-label={t("Sprache")}>
            {(["en", "de"] as const).map((code) => (
              <button
                key={code}
                type="button"
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="admin-user" ref={userMenuRef}>
            <button
              type="button"
              className="admin-user__button"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              <span className="admin-user__avatar">{username.slice(0, 2)}</span>
              {username}
              <ChevronDown size={14} />
            </button>

            {userMenuOpen && (
              <div className="admin-user__menu">
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setShowChangePassword(true);
                  }}
                >
                  <Lock size={15} /> {t("Change password")}
                </button>
                <button type="button" onClick={handleLogout}>
                  <LogOut size={15} /> {t("Logout")}
                </button>
              </div>
            )}
          </div>
        </header>

        <main className={`admin-content${WIDE_VIEWS.includes(activeView) ? " admin-content--wide" : ""}`}>
          {/* Problem z wysyłką maili (np. wygasający dostęp do Graph) widzi każdy zalogowany */}
          {activeView !== "Mail delivery" && (
            <MailStatusBanner
              onOpen={checkViewAccess("Mail delivery", permissions) ? () => setActiveView("Mail delivery") : undefined}
            />
          )}
          {renderView()}
        </main>
      </div>

      {showChangePassword && <ChangePasswordDialog onClose={() => setShowChangePassword(false)} />}
      </DialogProvider>
    </div>
  );
};

const AdminPanel: React.FC = () => {
  return (
    <LanguageProvider>
      <AdminPanelContent />
    </LanguageProvider>
  );
};

export default AdminPanel;
